import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import debounce from "lodash.debounce";
import { DataTableProvider } from "components/DataTable";
import { PAGINATION_TYPE } from "components/Pagination";
import { RenderMemberList } from "components/DisplayBox";
import { GET_PROFILES } from "graphql/queries";
import { Grid } from "components/Containers";
import { SearchInput } from "components/CodeChallenges";

const keyName = "profiles";

/**
 * ProfilesList
 * Multi-select list of profiles for adding to termsheet, teams, lists, etc.
 *
 * @param {Array}     allowedFilters - Filters to enable (e.g., ['search'])
 * @param {Object}    variables      - Additional query variables
 */
const ProfilesList = ({ allowedFilters = [], variables, ...props }) => {
  const [keyword, setKeyword] = useState();
  const [debounceKeyword, setDebounceKeyword] = useState();

  const debouncedSetKeyword = useMemo(() => debounce((val) => setDebounceKeyword(val), 500), []);

  useEffect(() => {
    return () => {
      debouncedSetKeyword.cancel();
    };
  }, [debouncedSetKeyword]);

  const handleSearchInputChange = (val) => {
    setKeyword(val);
    debouncedSetKeyword(val);
  };

  return (
    <Grid>
      {allowedFilters.includes("search") && (
        <Grid.col start={1} end={6}>
          <SearchInput
            placeholder="Type to search..."
            value={keyword}
            onChange={(_name, val) => handleSearchInputChange(val)}
            type="search"
          />
        </Grid.col>
      )}
      <Grid.col start={1} end={13}>
        <DataTableProvider
          keyName={keyName}
          queryName={GET_PROFILES}
          columnData={COLUMN_DATA}
          paginationType={PAGINATION_TYPE.classic}
          paginationProps={{ countMessage: "Global.Profiles.Count" }}
          variables={{
            ...variables,
            filters: {
              search: debounceKeyword,
              ...variables?.filters,
            },
          }}
          {...props}
        />
      </Grid.col>
    </Grid>
  );
};

const COLUMN_DATA = [
  {
    label: <FormattedMessage id="Profiles.ProfilesPicker.List.Member" />,
    cell: (profile) => <RenderMemberList data={profile} />,
  },
];

ProfilesList.propTypes = {
  allowedFilters: PropTypes.array,
  variables: PropTypes.object,
};

export default ProfilesList;
